import { HttpTransportType, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import type { HubConnection } from '@microsoft/signalr';
import { sleep } from '@sports-utils/timer';
import { useMemoize } from '@vueuse/core';
import { ref } from 'vue';
import { formatBetBuilderRecommendation } from '@/core/lib/betBuilderRecommendationHelper';
import { createRetryPolicy } from '@/core/lib/retryPolicy';
import type { IBetBuilderRecommendation } from '@/interface/betBuilder';
import type { RogueModelsBetBuilderPreBuilt } from '@/interface/rogue';
import { useToggleStore } from '@/store/toggleStore';

type UpdateCallback = (data: IBetBuilderRecommendation) => void;
type DeleteCallback = () => void;

interface Subscriber {
    onUpdate: UpdateCallback;
    onDelete: DeleteCallback;
}

const connection = ref<HubConnection | null>(null);
const subscribers = new Map<string, Set<Subscriber>>();

export const isBetBuilderWsConnected = ref(false);

let isStopping = false;

async function safeInvoke(method: string, key: string): Promise<void> {
    if (isStopping) return;
    if (connection.value?.state !== HubConnectionState.Connected) return;
    await connection.value.invoke(method, key);
}

const retryPolicy = createRetryPolicy({ prefix: 'BetBuilderHub' });

function isStartStoppedError(err: unknown): boolean {
    const message = err instanceof Error ? err.message : String(err);
    return message.includes('Failed to start the HttpConnection before stop() was called.');
}

const startConnection = useMemoize(async () => {
    const connectionHub = connection.value ?? createConnection();
    connection.value = connectionHub;
    try {
        await connectionHub.start();
        retryPolicy.reset();
        isBetBuilderWsConnected.value = true;
    } catch (err) {
        connection.value = null;
        isBetBuilderWsConnected.value = false;
        if (isStartStoppedError(err)) {
            retryPolicy.reset();
            return;
        }
        startConnection.clear();
        await retryPolicy.handleError(err, () => startConnection());
    } finally {
        startConnection.clear();
    }
});

function createConnection() {
    const { recommendationWsUrl } = useToggleStore();
    const hubUrl = `${recommendationWsUrl}/hubs/recommendationHub`;
    const conn = new HubConnectionBuilder()
        .withUrl(hubUrl, {
            skipNegotiation: true,
            transport: HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Error)
        .build();

    conn.on('BetBuilderPreBuiltUpdated', (raw: RogueModelsBetBuilderPreBuilt) => {
        const data = formatBetBuilderRecommendation(raw);
        const { combinationKey } = data;
        subscribers.get(combinationKey)?.forEach(s => s.onUpdate(data));
    });

    conn.on('BetBuilderPreBuiltDeleted', (key: string) => {
        subscribers.get(key)?.forEach(s => s.onDelete());
    });

    conn.onreconnecting((err) => {
        isBetBuilderWsConnected.value = false;
        console.warn('[BetBuilderHub] Connection lost, attempting to reconnect...', err);
    });

    conn.onreconnected(async () => {
        isBetBuilderWsConnected.value = true;
        const keys = Array.from(subscribers.keys());
        await Promise.allSettled(keys.map(key => safeInvoke('SubscribeBetBuilderPreBuilt', key)));
    });

    conn.onclose(async (err) => {
        isBetBuilderWsConnected.value = false;
        if (err) {
            console.error('[BetBuilderHub] Connection permanently closed with error', err);
        }
        connection.value = null;

        if (isStopping) {
            isStopping = false;
            return;
        }

        if (subscribers.size > 0) {
            try {
                await tryConnect();
                await Promise.allSettled(
                    Array.from(subscribers.keys()).map(key => safeInvoke('SubscribeBetBuilderPreBuilt', key)),
                );
            } catch (e) {
                console.error('[BetBuilderHub] Failed to reconnect after close', e);
            }
        }
    });

    return conn;
}

async function tryConnect() {
    const { recommendationWsUrl } = useToggleStore();
    if (!recommendationWsUrl) return;
    if (connection.value !== null && connection.value.state !== HubConnectionState.Disconnected) return;

    await startConnection();
}

async function subscribe(key: string, callbacks: Subscriber) {
    await tryConnect();

    let set = subscribers.get(key);
    if (!set) {
        set = new Set();
        subscribers.set(key, set);
    }
    set.add(callbacks);

    await safeInvoke('SubscribeBetBuilderPreBuilt', key);
}

async function unsubscribe(key: string, callbacks: Subscriber) {
    const set = subscribers.get(key);
    if (!set) return;

    set.delete(callbacks);
    if (set.size === 0) {
        subscribers.delete(key);
        try {
            await safeInvoke('UnsubscribeBetBuilderPreBuilt', key);
        } catch {
            // Ignored: connection may already be closing during component unmount
        }
    }

    if (subscribers.size === 0 && connection.value) {
        const currentConnection = connection.value;

        // Wait for 1 second to let unsubscribe complete before stopping connection,
        // to prevent unsubscribe then subscribe behavior
        await sleep(1000);
        if (subscribers.size > 0 || connection.value !== currentConnection) return;

        isStopping = true;

        try {
            await currentConnection.stop();
        } catch {
            // Ignored: SignalR's stop() rejects when pending invocations are cancelled during shutdown
        }
        if (connection.value === currentConnection) {
            connection.value = null;
        }
    }
}

export function useBetBuilderHub() {
    return {
        subscribe,
        unsubscribe,
    };
}
